import React, { useState, useEffect, useMemo ,useCallback} from 'react';
import { NavLink  } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { jsPDF } from 'jspdf';
import debounce from "lodash.debounce";
import eventService from '../../services/EventService';
import Nav from '../../components/event/NavEvent';
import Slide from '../../components/event/SlideActionEvent';
import "../../static/css/datatable.css";

export default function Event({ itemsPerPage = 25 }) {
    const defaultValues = {
        coming: false,
    };
    const comingValues = {
        coming: true,
        search:""
    };
    const { register, control, watch, handleSubmit, reset, getValues } = useForm({
        defaultValues
    });
    const [loading, setLoading] = useState(true);
    const [results, setResults] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPageLocal, setItemsPerPageLocal] = useState(itemsPerPage);
    const [isSlideOpen, setIsSlideOpen] = useState(false);
    const [event, setEvent] = useState({});
    const [loadSlide, setLoadSlide] = useState(false);

    const searchValue = watch("search", "");
    const searchFields = ["name", "city"];
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

    const totalPages = Math.ceil(filteredData.length / itemsPerPageLocal);
    const startIndex = (currentPage - 1) * itemsPerPageLocal;
    const endIndex = startIndex + itemsPerPageLocal;
    const currentItems = filteredData.slice(startIndex, endIndex);
    // Funzione di navigazione
    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const onDefault = () => {
        reset(defaultValues);
        fetchEvents(getValues());
    };
    const onComing = () => {
        setSortConfig({ key: null, direction: null });
        reset(comingValues);
        fetchEvents(getValues());
    };
    const onFetchEventsAfterEdit = () => {
        fetchEvents(getValues());
    };
    const fetchEvents= async (data) => {
        try {
            const res = await eventService.getFilter(data);
            // Estrarre i dati specifici dalla struttura complessa di response.data
            const resultsData = res.data || [];
            const total = res.data.length || 0;

            setResults(resultsData); // Salva i risultati
            setFilteredData(resultsData);
            setTotalResults(total); // Salva il totale dei risultati
            setLoading(false);
            // Calcoliamo dinamicamente itemsPerPageLocal dopo aver ricevuto i dati
            const defaultItemsPerPage = Math.min(itemsPerPage, total);
            setItemsPerPageLocal(defaultItemsPerPage);

        } catch (error) {
            console.error('Error fetching events', error);
        }
    };

    useEffect(() => {
        fetchEvents(getValues());
    }, []);

    const filterData = useCallback((term) => {
        // se il termine è vuoto, lista originale
        if (!term || term.trim() === "") {
            const sorted = applySort(results, sortConfig);
            setFilteredData(sorted);
            return;
        }

        const lower = term.toLowerCase();

        const filtered = results.filter(item =>
            searchFields.some(field => {
                let value = item[field];
                if (!value) return false;
                return value.toString().toLowerCase().includes(lower);
            })
        );

        // applica ordinamento anche ai risultati filtrati
        const sorted = applySort(filtered, sortConfig);

        setFilteredData(sorted);
        setCurrentPage(1);
    }, [results, sortConfig, searchFields]);

    const applySort = (data, sortCfg) => {
        const { key, direction } = sortCfg;

        if (!key || !direction) return data;

        return [...data].sort((a, b) => {
            let valA = a[key];
            let valB = b[key];

            if (key === "invoice" || key === "taxable") {
                valA = isNaN(valA) ? -Infinity : Number(valA);
                valB = isNaN(valB) ? -Infinity : Number(valB);
            } else if (key === "startDate") {
                valA = valA ? new Date(valA).getTime() : -Infinity;
                valB = valB ? new Date(valB).getTime() : -Infinity;
            }  else {
                valA = valA?.toString().toLowerCase() || "";
                valB = valB?.toString().toLowerCase() || "";
            }

            if (valA < valB) return direction === "asc" ? -1 : 1;
            if (valA > valB) return direction === "asc" ? 1 : -1;
            return 0;
        });
    };

    const sortData = (key) => {
        let direction = "asc";
        if (sortConfig.key === key) {
            if (sortConfig.direction === "asc") direction = "desc";
            else if (sortConfig.direction === "desc") direction = null; // reset
        }
        const newSort = { key, direction };
        setSortConfig(newSort);

        // reset → torna tutti i dati filtrati senza sorting
        if (!direction) {
            filterData(searchValue);   // lascia filtro attivo ma senza ordinamento
            return;
        }
        const sorted = applySort(filteredData, newSort);
        setFilteredData(sorted);
    };

    const debouncedFilter = useMemo(
        () => debounce((term) => filterData(term), 300),
        [filterData]     // dipende SOLO da filterData
    );
    useEffect(() => {
        debouncedFilter(searchValue);
        return () => debouncedFilter.cancel();
    }, [searchValue, debouncedFilter]);

    useEffect(() => { //Quando results cambia per filtri applicati ricalcola la lista corretta:
        const sorted = applySort(results, sortConfig);
        setFilteredData(sorted);
    }, [results]);

    useEffect(() => { //Effetto che aggiorna i totali
        const total = filteredData.length;
        setTotalResults(total);

        if (total > 0) {
            setItemsPerPageLocal(Math.min(itemsPerPage, total));
        }
    }, [filteredData]);

    // Funzione per aprire/chiudere il pannello laterale
    const toggleSlide = (event) => {
        setLoadSlide(true);
        if (event) {
            setEvent(event);
        } else {
            setEvent(
                Object.keys(event || {}).reduce((acc, key) => {
                    acc[key] = ""; // Imposta tutti i campi a stringa vuota
                    return acc;
                }, {})
            );
         }

        setIsSlideOpen(!isSlideOpen);
    };
    // Funzione per aggiornare il numero di elementi per pagina
    const handleItemsPerPageChange = (event) => {
        setItemsPerPageLocal(parseInt(event.target.value, 10));
        setCurrentPage(1);
    };
    // Funzione per generare le opzioni dinamiche per la select
    const generateItemsPerPageOptions = () => {
        const options = [];
        const maxItems = totalResults;

        if (maxItems <= 2) {
          options.push(2);
        } else if (20 <= maxItems && maxItems <= 50){
          options.push(10, 25, maxItems);
        } else if (maxItems > 50) {
            options.push(10, 25, 50);
        } else {
            // Aggiungiamo anche multipli di 2 tra il minimo e il massimo
            for (let i = 2; i <= maxItems; i *= 2) {
                if (i < maxItems) options.push(i);
            }
            // Se il numero totale è abbastanza grande, aggiungiamo anche il numero massimo
            if (maxItems > 2) options.push(maxItems);
        }

        return options;
    };
    const getFormattedDate = () => {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}-${month}-${year}`;
    };
    const handleExportPDF = () => {
        const doc = new jsPDF('l', 'mm', 'a4');//configuro con l(stampa orizzontale)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.text("Lista Eventi", 14, 10); //Titolo
        doc.autoTable({
            head: [['NR','Evento','Contatti','Montaggio','Smontaggio','Vettore','Note']], // Intestazioni della tabella
            body: results.map((event, index) => [index + 1,`${event.name}\n${event.year}\n${event.address}\n${event.city}\n${event.startDate} ${event.endDate}`,
                `Telefono: ${event.phone}\nEmail: ${event.email}`,
                `Inizio: ${event.startAssembly}\nFine ${event.endAssembly}`,
                `Inizio: ${event.startDisassembly}\nFine: ${event.endDisassembly}`,
                `Nome: ${event.vector ? event.vector.name : 'Non selezionato'}\nIndirizzo: ${event.vector ? event.vector.address : 'Non selezionato'}`,
                event.note]), // Corpo della tabella
            startY: 20, // Imposta la posizione di inizio della tabella
            theme: 'grid', // Aggiungi griglia alla tabella
        });
        doc.save(`event_${getFormattedDate()}.pdf`);
    };

  	return (
  	<>
  	    <div className="row">
            <div className="col-md-12">
                {/* Nav Eventi */}
                <Nav />
            </div>
        </div>
        <div className="row text-nowrap">
            <div className="col-md-12 mb-6">
                <div className="card card-action">
                    <div className="card-header align-items-center">
                        <h5 className={`card-action-title mb-0 cursor-pointer`} >
                            <i className="bx bx-slider me-2 bx-sm"></i>Filtri
                        </h5>
                        <div className="card-action-element">
                            <button type="button" className="btn btn-primary btn-sm edit-address me-3" onClick={onDefault}>Default</button>
                            <button type="button" className="btn btn-label-danger btn-sm edit-address" onClick={onComing}>Futuri</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
  	    <div className="card">
            {/* List Eventi */}
            <div className="card-datatable table-responsive">
                <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                    <div className="row">
                        <div className="col-md-2">
                            <div className="dataTables_length">
                                <label>
                                    <select name="DataTables_Table_0_length" className="form-select" id="itemsPerPage"
                                        value={itemsPerPageLocal} onChange={handleItemsPerPageChange}>
                                        {generateItemsPerPageOptions().map((value) => (
                                            <option key={value} value={value}>{value}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                        <div className="col-md-10">
                            <div className="dt-action-buttons text-xl-end text-lg-start text-md-end text-start d-flex align-items-center justify-content-end flex-md-row flex-column mb-6 mb-md-0 mt-n6 mt-md-0">
                                <div className="dataTables_filter">
                                    <label>
                                        <input className="form-control" type="text"
                                            placeholder="Cerca per ..."
                                            {...register('search')}
                                        />
                                    </label>
                                </div>
                                <div className="dt-buttons btn-group flex-wrap">
                                    <div className="btn-group">
                                        <button onClick={handleExportPDF} className="btn buttons-collection dropdown-toggle btn-label-secondary mx-4" type="button" >
                                            <span><i className="bx bx-export me-2 bx-sm"></i>Export</span>
                                        </button>
                                    </div>
                                    {/* Pulsante per aprire il modulo di aggiunta evento */}
                                    <button className="btn add-new btn-primary" onClick={() => toggleSlide(null)}>
                                         <span>
                                            <i className="bx bx-plus bx-sm me-0 me-sm-2"></i>
                                            <span className="d-none d-sm-inline-block">Aggiungi</span>
                                         </span>
                                   </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <table className="datatables-users table border-top dataTable no-footer dtr-column">
                        <thead>
                            <tr>
                                <th onClick={() => sortData("name")}>
                                    NOME {sortConfig.key === "name" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th onClick={() => sortData("city")}>
                                    CITT{'à'.toUpperCase()} {sortConfig.key === "city" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th onClick={() => sortData("startDate")}>
                                    INIZIO {sortConfig.key === "startDate" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th>FINE</th>
                                <th>AZIONI</th>
                            </tr>
                        </thead>
                        {loading && (
                            <tbody>
                                <tr className="odd">
                                    <td className="dataTables_empty">Loading...</td>
                                </tr>
                            </tbody>
                        )}
                        <tbody>
                             {currentItems.map((result,index) => (
                                <tr key={result.id_event}>
                                    <td>
                                        <NavLink to={`/event-detail/${result.id_event}`} >
                                            {result.name} - {result.year}
                                        </NavLink>
                                    </td>
                                    <td>{result.city}</td>
                                    <td>{result.startDate ? format(new Date(result.startDate), 'dd MMMM yyyy', { locale: it }) : ''}</td>
                                    <td>{result.endDate ? format(new Date(result.endDate), 'dd MMMM yyyy', { locale: it }) : ''}</td>
                                    <td>
                                        <a className="dropdown-item" onClick={() => toggleSlide(result)}>
                                            <i className="bx bx-edit-alt me-1"></i>
                                        </a>
                                    </td>
                                </tr>
                              ))}
                         </tbody>
                    </table>
                    <div className="row">
                        <div className="col-sm-12 col-md-6">
                            <div className="dataTables_info" role="status" aria-live="polite">
                                Risultati ottenuti : {totalResults}
                            </div>
                        </div>
                        <div className="col-sm-12 col-md-6">
                            <div className="dataTables_paginate paging_simple_numbers custom-ryo">
                                {!loading && (
                                    <div className="pagination">
                                        <button className="page-link" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                                            <i className="bx bx-chevron-left bx-18px"></i>
                                        </button>
                                        {Array.from({ length: totalPages }, (_, index) => (
                                            <button
                                              key={index + 1}
                                              onClick={() => goToPage(index + 1)}
                                              disabled={currentPage === index + 1}
                                              className={`page-link ${currentPage === index + 1  ? "current-page" : ""}`}
                                            >
                                              {index + 1}
                                            </button>
                                        ))}
                                        <button className="page-link" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                                            <i className="bx bx-chevron-right bx-18px"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Slide */}
            { loadSlide && event && (
                <Slide show={isSlideOpen} onClose={toggleSlide} onFetchEvents={onFetchEventsAfterEdit} search={searchValue} event={event}/>
                )
            }
            {/* Slide */}
            {/* /List Eventi*/}
  	    </div>
    </>
	);
}